import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import Card from '../UI/Card';

interface ChartData {
  month: string;
  profit: number;
  expense: number;
  income: number;
}

interface FinancialChartsProps {
  data: ChartData[];
}

const FinancialCharts: React.FC<FinancialChartsProps> = ({ data }) => {
  const formatCurrency = (value: number) => `ETB ${value.toLocaleString()}`;

  const maxProfit = Math.max(...data.map((d) => d.profit), 0);
  const maxExpense = Math.max(...data.map((d) => d.expense), 0);
  const maxIncome = Math.max(...data.map((d) => d.income), 0);
  const maxValue = Math.max(maxProfit, maxExpense, maxIncome, 100);

  const getMaxDomain = (value: number) => {
    if (value === 0) return 100;
    return Math.ceil(value / 100) * 100 + 50;
  };

  const domainMax = getMaxDomain(maxValue);

  const makeTooltip =
    (label: string, color: string) =>
    ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-600 mb-1">
              {payload[0].payload.month}
            </p>
            <p className={`text-sm font-semibold ${color}`}>
              {label}: ETB {payload[0]?.value?.toLocaleString() || 0}
            </p>
          </div>
        );
      }
      return null;
    };

  const ProfitTooltip = makeTooltip('Profit', 'text-green-600');
  const ExpenseTooltip = makeTooltip('Expense', 'text-red-600');
  const IncomeTooltip = makeTooltip('Income', 'text-blue-600');

  const renderChart = (
    title: string,
    subtitle: string,
    dataKey: 'profit' | 'expense' | 'income',
    gradientId: string,
    color: string,
    dotColor: string,
    tooltip: any
  ) => (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1">
          <div className={`w-3 h-3 rounded-full ${dotColor}`} />
          <span className="text-xs text-gray-500">{title.split(' ')[1] || title}</span>
        </div>
      </div>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatCurrency}
              width={60}
              domain={[0, domainMax]}
            />
            <Tooltip content={tooltip} />
            <Bar dataKey={dataKey} radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((_, i) => (
                <Cell key={`c-${i}`} fill={`url(#${gradientId})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">Total {title.split(' ')[1] || title}</p>
          <p className="text-lg font-bold text-gray-900">
            ETB {data.reduce((s, d) => s + d[dataKey], 0).toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Monthly Average</p>
          <p className="text-sm font-semibold text-gray-900">
            ETB{' '}
            {data.length
              ? (data.reduce((s, d) => s + d[dataKey], 0) / data.length).toLocaleString()
              : 0}
          </p>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      {renderChart(
        'Monthly Profit Dividend',
        '',
        'profit',
        'profitGradient',
        '#10B981',
        'bg-green-500',
        ProfitTooltip
      )}
      {renderChart(
        'Monthly Expenses',
        '',
        'expense',
        'expenseGradient',
        '#EF4444',
        'bg-red-500',
        ExpenseTooltip
      )}
      <div className="lg:col-span-2">
        {renderChart(
          'Monthly Income',
          'Last 4 months',
          'income',
          'incomeGradient',
          '#3B82F6',
          'bg-blue-500',
          IncomeTooltip
        )}
      </div>
    </div>
  );
};

export default FinancialCharts;